/**
 * Admissions enquiries from the public form (POST /public/forms/admissions-enquiry).
 * Personal data — gated by `enquiries:read`, which the viewer role does not get.
 *
 * Status is filtered server-side (`?status=`) so the CSV export and the table
 * always agree; search runs client-side over the loaded rows.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { Download, Mail, Phone, StickyNote, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import { api, BASE } from "@/lib/api";
import { fetchAll, LOG_MAX_ROWS } from "@/lib/fetchAll";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DataTable } from "@/components/data-table/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { TruncationNotice } from "@/components/TruncationNotice";

export const Route = createFileRoute("/_app/enquiries/")({ component: EnquiriesPage });

type EnquiryStatus = "new" | "contacted" | "closed";

interface EnquiryRow {
  id: number;
  name: string;
  email: string;
  phone: string;
  programme: string | null;
  city: string | null;
  message: string | null;
  source_path: string | null;
  utm_json: string | null;
  status: EnquiryStatus;
  notes: string | null;
  created_at: number;
}

const STATUS_OPTIONS: { value: EnquiryStatus; label: string }[] = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "closed", label: "Closed" },
];
const ALL = "__all__";

function StatusPill({ status }: { status: EnquiryStatus }) {
  const variant = status === "new" ? "warning" : status === "contacted" ? "default" : "outline";
  return <Badge variant={variant}>{STATUS_OPTIONS.find((s) => s.value === status)?.label ?? status}</Badge>;
}

function EnquiriesPage() {
  const { can } = useAuth();
  const [status, setStatus] = useState<string>("new");
  const [open, setOpen] = useState<EnquiryRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["enquiries", "list", status],
    queryFn: () =>
      fetchAll<EnquiryRow>("/admin/enquiries", {
        query: { status: status === ALL ? undefined : status },
        maxRows: LOG_MAX_ROWS,
      }),
  });

  const exportHref = `${BASE}/admin/enquiries/export.csv${status === ALL ? "" : `?status=${status}`}`;

  const columns: ColumnDef<EnquiryRow, unknown>[] = [
    {
      accessorKey: "created_at",
      header: "Received",
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-sm">{format(row.original.created_at * 1000, "MMM d, h:mma")}</span>
      ),
    },
    {
      // Accessor folds email and message in so the global search covers them.
      id: "name",
      accessorFn: (r) => `${r.name} ${r.email} ${r.message ?? ""}`,
      header: "Name",
      cell: ({ row }) => (
        <div>
          <p className="font-medium">{row.original.name}</p>
          <p className="text-xs text-muted-foreground">{row.original.email}</p>
        </div>
      ),
    },
    { accessorKey: "phone", header: "Phone", cell: ({ row }) => <span className="whitespace-nowrap">{row.original.phone}</span> },
    { accessorKey: "programme", header: "Programme", cell: ({ row }) => row.original.programme ?? <span className="text-muted-foreground">—</span> },
    { accessorKey: "city", header: "City", cell: ({ row }) => row.original.city ?? <span className="text-muted-foreground">—</span> },
    { accessorKey: "status", header: "Status", cell: ({ row }) => <StatusPill status={row.original.status} /> },
    {
      id: "notes",
      header: "",
      cell: ({ row }) => (row.original.notes ? <StickyNote className="h-4 w-4 text-muted-foreground" aria-label="Has notes" /> : null),
    },
  ];

  return (
    <div className="space-y-2">
      <PageHeader
        title="Enquiries"
        description="Admissions enquiries from the website form. Click a row to read it, change its status or add notes."
        actions={
          <>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-9 w-[160px]" aria-label="Status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All statuses</SelectItem>
                {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {can("enquiries", "export") && (
              <Button asChild variant="outline">
                <a href={exportHref} download>
                  <Download className="h-4 w-4" /> Export CSV
                </a>
              </Button>
            )}
          </>
        }
      />
      {data?.truncated && <TruncationNotice count={data.items.length} noun="enquiries" />}
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        globalSearch
        globalSearchPlaceholder="Search name, email, phone, programme…"
        onRowClick={(r) => setOpen(r)}
        emptyMessage={isLoading ? "Loading…" : "No enquiries with this status."}
      />
      <EnquiryDialog row={open} onClose={() => setOpen(null)} />
    </div>
  );
}

function EnquiryDialog({ row, onClose }: { row: EnquiryRow | null; onClose: () => void }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState<EnquiryStatus>("new");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (row) {
      setStatus(row.status);
      setNotes(row.notes ?? "");
    }
  }, [row]);

  const save = useMutation({
    mutationFn: () => api.patch(`/admin/enquiries/${row!.id}`, { status, notes: notes.trim() || null }),
    onSuccess: () => {
      toast.success("Enquiry updated");
      qc.invalidateQueries({ queryKey: ["enquiries"] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });
  const remove = useMutation({
    mutationFn: () => api.del(`/admin/enquiries/${row!.id}`),
    onSuccess: () => {
      toast.success("Enquiry deleted");
      qc.invalidateQueries({ queryKey: ["enquiries"] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Delete failed"),
  });

  let utm: Record<string, string> | null = null;
  try {
    utm = row?.utm_json ? (JSON.parse(row.utm_json) as Record<string, string>) : null;
  } catch {
    utm = null;
  }

  return (
    <Dialog open={!!row} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl">
        {row && (
          <>
            <DialogHeader>
              <DialogTitle>{row.name}</DialogTitle>
              <DialogDescription>
                Enquiry #{row.id} · received {format(row.created_at * 1000, "d MMM yyyy, h:mma")}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap gap-2">
                <a className="inline-flex items-center gap-1 rounded-md border px-2 py-1 hover:bg-secondary" href={`mailto:${row.email}`}>
                  <Mail className="h-3.5 w-3.5" /> {row.email}
                </a>
                <a className="inline-flex items-center gap-1 rounded-md border px-2 py-1 hover:bg-secondary" href={`tel:${row.phone}`}>
                  <Phone className="h-3.5 w-3.5" /> {row.phone}
                </a>
              </div>
              <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1">
                <dt className="text-muted-foreground">Programme</dt><dd>{row.programme ?? "—"}</dd>
                <dt className="text-muted-foreground">City</dt><dd>{row.city ?? "—"}</dd>
                <dt className="text-muted-foreground">Page</dt><dd className="break-all">{row.source_path ?? "—"}</dd>
                {utm && Object.keys(utm).length > 0 && (
                  <>
                    <dt className="text-muted-foreground">Campaign</dt>
                    <dd className="break-all">{Object.entries(utm).map(([k, v]) => `${k}=${v}`).join(" · ")}</dd>
                  </>
                )}
              </dl>
              {row.message && (
                <blockquote className="whitespace-pre-wrap rounded-md bg-secondary px-3 py-2">{row.message}</blockquote>
              )}
              <div className="grid gap-3 sm:grid-cols-[10rem_1fr]">
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <Select value={status} onValueChange={(v) => setStatus(v as EnquiryStatus)} disabled={!can("enquiries", "update")}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="enq-notes">Notes</Label>
                  <Textarea
                    id="enq-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="Called on …, asked about hostel fees…" disabled={!can("enquiries", "update")}
                  />
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2 sm:justify-between">
              {can("enquiries", "delete") ? (
                <Button type="button" variant="ghost" className="text-destructive"
                  onClick={() => { if (confirm("Delete this enquiry permanently?")) remove.mutate(); }}>
                  <Trash2 className="h-4 w-4" /> Delete
                </Button>
              ) : <span />}
              <div className="flex gap-2">
                <Button type="button" variant="ghost" onClick={onClose}>Close</Button>
                {can("enquiries", "update") && (
                  <Button type="button" disabled={save.isPending} onClick={() => save.mutate()}>
                    {save.isPending ? "Saving…" : "Save"}
                  </Button>
                )}
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
